import { FC, Fragment, memo, useMemo } from "react";
import { FormikHandlers } from "formik";
import * as yup from "yup";
import {
  AutocompleteFilterBody,
  AutocompleteFilterBodyProps,
} from "../AutocompleteFilterBody";
import { FilterItemOperatorType } from "../../../FilterItemOperatorType";
import {
  FilterItem,
  Operator,
  useGetUsersMeOrganisationsCurrentByTypeResourcesTypesQuery,
} from "@/services/cloudchipr.api";
import { multipleValueSelectOperators } from "@/components/pages/common/filters/utils/constants/filter-types/autocomplete-multipe-selection-operator-types";

export type VolumeResourceType = "ebs" | "disk" | "az_disk";

interface VolumeTypeFilterProps extends AutocompleteFilterBodyProps {
  filter: FilterItem;
  operators?: Operator[];
  onChange: FormikHandlers["handleChange"];
  resourceType: VolumeResourceType;
}

export const VolumeTypeFilterValueValidation = yup.mixed().when("operator", {
  is: (operator: string) => multipleValueSelectOperators.includes(operator),
  then: yup.array().min(1).required(),
  otherwise: yup.string().required(),
});

export const VolumeTypeFilter: FC<VolumeTypeFilterProps> = memo(
  ({ operators, filter, onChange, resourceType, ...props }) => {
    const { data, isLoading } =
      useGetUsersMeOrganisationsCurrentByTypeResourcesTypesQuery({
        type: resourceType,
      });

    const renderOptions = useMemo(() => {
      return data?.map((item) => {
        return {
          value: item,
          label: item,
        };
      });
    }, [data]);

    const filterLabel = filterLabels[resourceType];

    return (
      <Fragment>
        <FilterItemOperatorType
          onChange={onChange}
          options={operators}
          value={filter.operator || "in"}
        />

        <AutocompleteFilterBody
          {...props}
          options={renderOptions ?? []}
          isLoading={isLoading}
          label={filterLabel}
          selectedValue={Array.isArray(filter?.value) ? filter.value : []}
        />
      </Fragment>
    );
  },
);

const filterLabels: Record<VolumeResourceType, string> = {
  disk: "Disk Type",
  az_disk: "Disk SKU",
  ebs: "Type",
};
